import { Button, Col, Form, FormInstance, Input, message, Popconfirm, Row, Select, Space, Tabs } from 'antd';
import Icon from '@ant-design/icons';
import { ReactComponent as SaveSvg } from '../../../../resources/images/save.svg';
import { ReactComponent as TrashCanSvg } from '../../../../resources/images/trash-can.svg';
import React, { Component } from 'react';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Link, RouteComponentProps } from 'react-router-dom';
import connectionApi from '../../../../api/ConnectionApi';
import LayoutComponent from '../../../../components/LayoutComponent/LayoutComponent';
import CustomContext from '../../../../context/CustomContext';
import { Connection, Hub } from '../../../../model/entities';
import notificationService from '../../../../services/NotificationService';
import WrapperComponent from '../../../../components/WrapperComponent/WrapperComponent';
import TextArea from 'antd/lib/input/TextArea';
import hubApi from '../../../../api/HubApi';
import ConnectionLinksComponent from './ConnectionLinksComponent/ConnectionLinksComponent';
import ConnectionTasksComponent from './ConnectionTasksComponent/ConnectionTasksComponent';
import stringService from '../../../../services/StringService';
import ConnectionSchedulesComponent from './ConnectionSchedulesComponent/ConnectionSchedulesComponent';
import { workDocsOAuth2Enabled } from '../../../../api/CustomAxios';

class ConnectionPage extends Component<Props, State> {
    static contextType = CustomContext;
    context!: React.ContextType<typeof CustomContext>;
    formRef = React.createRef<FormInstance>();

    constructor(props: Props) {
        super(props);
        this.state = {
            hubs: [],
        };
    }

    componentDidMount() {
        this.init();
    }

    /** METHODS **/

    init = async () => {
        try {
            if (this.props.match.params.id === 'new') {
                await this.new();
            } else {
                await this.get(+this.props.match.params.id);
            }
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        }
    };

    new = async () => {
        const connection: Connection = {};
        const hubs = await hubApi.list();
        this.setState({ connection, hubs });
        this.formRef.current!.setFieldsValue(connection);
    };

    get = async (id: number) => {
        const responses = await Promise.all([connectionApi.get(id), hubApi.list()]);
        const connection = responses[0];
        const hubs = responses[1];
        this.setState({ connection, hubs });
        this.formRef.current!.setFieldsValue(connection);
    };

    save = async (values: any) => {
        const { hubs } = this.state;

        try {
            this.setState({ saving: true });
            const sourceHub = hubs.find((h) => h.id === values.source.id);
            const targetHub = hubs.find((h) => h.id === values.target.id);
            let connection: Connection = Object.assign({}, this.state.connection, values, {
                source: sourceHub,
                target: targetHub,
            });
            connection = connection.id
                ? await connectionApi.update(connection)
                : await connectionApi.create(connection);
            message.success(this.props.intl.formatMessage({ id: 'status.saved' }));
            this.setState({ connection });
        } catch (error) {
            notificationService.displayError(error, this.props.intl, [
                { status: 409, message: 'connection.status.duplicate' },
            ]);
        } finally {
            this.setState({ saving: false });
        }
    };

    delete = async () => {
        const { connection } = this.state;
        try {
            this.setState({ loading: 'deleting' });
            await connectionApi.delete(connection!);
            this.props.history.push(`/sync/connections`);
        } catch (error) {
            notificationService.displayError(error, this.props.intl);
        } finally {
            this.setState({ loading: false });
        }
    };

    /*** COMPONENTS ***/

    renderContent = (): React.ReactElement | undefined => {
        return (
            <>
                {this.renderForm()}
                {this.renderTabs()}
            </>
        );
    };

    renderTabs = (): React.ReactElement | undefined => {
        const { connection } = this.state;
        return (
            <Tabs defaultActiveKey="links" size="large">
                <Tabs.TabPane key="links" tab={<FormattedMessage id="connection.links" />}>
                    {connection && connection.id && <ConnectionLinksComponent connection={connection} />}
                </Tabs.TabPane>
                <Tabs.TabPane key="schedules" tab={<FormattedMessage id="connection.schedules" />}>
                    {connection && connection.id && <ConnectionSchedulesComponent connection={connection} />}
                </Tabs.TabPane>
                <Tabs.TabPane key="tasks" tab={<FormattedMessage id="connection.tasks" />}>
                    {connection && connection.id && <ConnectionTasksComponent connection={connection} />}
                </Tabs.TabPane>
            </Tabs>
        );
    };

    renderForm = (): React.ReactElement | undefined => {
        const { connection, hubs, saving, loading } = this.state;
        const sourceHubsOptions = hubs
            .filter((hub) => hub.provider !== 'WORKDOCS' || workDocsOAuth2Enabled)
            .sort((a, b) => stringService.sort(a.name, b.name))
            .map((hub) => (
                <Select.Option key={hub.id} value={hub.id}>
                    {hub.provider} - {hub.name}
                </Select.Option>
            ));
        const targetHubsOptions = hubs
            .filter((hub) => hub.provider !== 'WORKDOCS')
            .sort((a, b) => stringService.sort(a.name, b.name))
            .map((hub) => (
                <Select.Option key={hub.id} value={hub.id}>
                    {hub.provider} - {hub.name}
                </Select.Option>
            ));

        return (
            <Form ref={this.formRef} onFinish={this.save} colon={false} layout="vertical">
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item
                            label={<FormattedMessage id="connection.name" />}
                            name="name"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input maxLength={100} size="large" />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={24}>
                        <Form.Item label={<FormattedMessage id="connection.description" />} name="description">
                            <TextArea maxLength={300} size="large" rows={3} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[28, 0]}>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="connection.source" />}
                            name={['source', 'id']}
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select size="large" disabled={connection && !!connection.id}>
                                {sourceHubsOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item
                            label={<FormattedMessage id="connection.target" />}
                            name={['target', 'id']}
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select size="large" disabled={connection && !!connection.id}>
                                {targetHubsOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item className="buttons">
                    <Space>
                        <Button
                            type="primary"
                            htmlType="submit"
                            size="large"
                            icon={<Icon component={SaveSvg} />}
                            loading={saving}
                            ghost={!!connection?.id}
                        >
                            <FormattedMessage id="button.save" tagName="span" />
                        </Button>
                        <Popconfirm
                            title={<FormattedMessage id="connection.delete" />}
                            onConfirm={this.delete}
                            okText={<FormattedMessage id="button.yes" />}
                            cancelText={<FormattedMessage id="button.no" />}
                        >
                            <Button
                                type="ghost"
                                size="large"
                                hidden={!connection || !connection.id}
                                icon={<Icon component={TrashCanSvg} />}
                                loading={loading === 'deleting'}
                            >
                                <FormattedMessage id="button.delete" tagName="span" />
                            </Button>
                        </Popconfirm>
                        <Link to="/sync/connections">
                            <Button type="text" size="large">
                                <FormattedMessage id="button.back" tagName="span" />
                            </Button>
                        </Link>
                    </Space>
                </Form.Item>
            </Form>
        );
    };

    render() {
        return (
            <LayoutComponent pageId="connections">
                <WrapperComponent title={<FormattedMessage id="connection.title" />}>
                    {this.renderContent()}
                </WrapperComponent>
            </LayoutComponent>
        );
    }
}
export default injectIntl(ConnectionPage);

type ParamsType = { id: string };
interface Props extends RouteComponentProps<ParamsType>, WrappedComponentProps {}

interface State {
    connection?: Connection;
    hubs: Hub[];
    saving?: boolean;
    loading?: false | 'deleting';
}
